export const HEIC_HEIF_MIME_TYPES = ["image/heic", "image/heif"];
export const HEIC_HEIF_EXTENSIONS = [".heic", ".heif"];

export const KYC_UPLOAD_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  ...HEIC_HEIF_MIME_TYPES,
  "application/octet-stream",
  "application/pdf",
];

export const KYC_UPLOAD_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ...HEIC_HEIF_EXTENSIONS,
  ".pdf",
];

export const KYC_DROPZONE_ACCEPTED_TYPES = [
  ...KYC_UPLOAD_MIME_TYPES,
  ...KYC_UPLOAD_EXTENSIONS,
];

// Investment dropzones have historically accepted any browser-recognized image
// plus PDF. Explicit extensions cover browsers that leave File.type empty.
export const INVESTMENT_UPLOAD_MIME_TYPES = [
  "image/*",
  ...HEIC_HEIF_MIME_TYPES,
  "application/pdf",
];
export const INVESTMENT_UPLOAD_EXTENSIONS = [...HEIC_HEIF_EXTENSIONS];
export const INVESTMENT_DROPZONE_ACCEPTED_TYPES = [
  ...INVESTMENT_UPLOAD_MIME_TYPES,
  ...INVESTMENT_UPLOAD_EXTENSIONS,
];

// The standalone deposit-slip input keeps its existing PNG/GIF/JPEG policy and
// adds only HEIC/HEIF.
export const DEPOSIT_SLIP_UPLOAD_MIME_TYPES = [
  "image/png",
  "image/x-png",
  "image/gif",
  "image/jpeg",
  ...HEIC_HEIF_MIME_TYPES,
];
export const DEPOSIT_SLIP_UPLOAD_EXTENSIONS = [
  ".png",
  ".gif",
  ".jpg",
  ".jpeg",
  ...HEIC_HEIF_EXTENSIONS,
];
export const DEPOSIT_SLIP_INPUT_ACCEPT = [
  ...DEPOSIT_SLIP_UPLOAD_MIME_TYPES,
  ...DEPOSIT_SLIP_UPLOAD_EXTENSIONS,
].join(",");

type FileDescriptor = Pick<File, "name" | "type">;
type CapturedFileData = string | ArrayBuffer | null | undefined;

export const UPLOAD_READ_ERROR_MESSAGE =
  "We couldn't read the selected file. Please select it again. If it is stored in iCloud, wait for it to download or save a local copy first.";

function decodeDataUrl(dataUrl: string): Uint8Array {
  const commaIndex = dataUrl.indexOf(",");
  if (
    !dataUrl.startsWith("data:") ||
    commaIndex < 0 ||
    !dataUrl.slice(0, commaIndex).endsWith(";base64")
  ) {
    throw new Error("Captured upload data is invalid");
  }

  const binary = atob(dataUrl.slice(commaIndex + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function canConstructFile(): boolean {
  try {
    return new File([], "support-check") instanceof File;
  } catch {
    return false;
  }
}

function extensionOf(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
}

export function isHeicHeifUpload(file: FileDescriptor): boolean {
  const mimeType = (file.type || "").trim().toLowerCase();
  return (
    HEIC_HEIF_MIME_TYPES.includes(mimeType) ||
    HEIC_HEIF_EXTENSIONS.includes(extensionOf(file.name))
  );
}

export function isAcceptedUploadFile(
  file: FileDescriptor,
  acceptedMimeTypes: string[],
  acceptedExtensions: string[]
): boolean {
  const mimeType = (file.type || "").trim().toLowerCase();
  if (mimeType) {
    return acceptedMimeTypes.some((acceptedType) => {
      const normalized = acceptedType.toLowerCase();
      return normalized.endsWith("/*")
        ? mimeType.startsWith(normalized.slice(0, -1))
        : mimeType === normalized;
    });
  }

  return acceptedExtensions
    .map((extension) => extension.toLowerCase())
    .includes(extensionOf(file.name));
}

export function isUsableInvestmentUpload(file: unknown): file is File {
  if (
    typeof File === "undefined" ||
    !(file instanceof File) ||
    file.size <= 0
  ) {
    return false;
  }

  const mimeType = (file.type || "").trim().toLowerCase();
  if (mimeType === "application/octet-stream") {
    return isHeicHeifUpload(file);
  }

  return isAcceptedUploadFile(
    file,
    INVESTMENT_UPLOAD_MIME_TYPES,
    INVESTMENT_UPLOAD_EXTENSIONS
  );
}

export async function materializeUploadFile(
  file: unknown,
  capturedData?: CapturedFileData
): Promise<File> {
  if (
    typeof File === "undefined" ||
    !(file instanceof File) ||
    file.size <= 0
  ) {
    throw new Error("Upload file is empty or unavailable");
  }

  if (!canConstructFile()) {
    // ponytail: Legacy browsers without a File constructor keep existing behavior.
    return file;
  }

  let bytes: BlobPart;
  if (typeof capturedData === "string") {
    bytes = decodeDataUrl(capturedData);
  } else if (
    capturedData instanceof ArrayBuffer ||
    Object.prototype.toString.call(capturedData) === "[object ArrayBuffer]"
  ) {
    bytes = capturedData as ArrayBuffer;
  } else {
    bytes = await file.arrayBuffer();
  }

  const materialized = new File([bytes], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
  if (materialized.size !== file.size) {
    throw new Error("Upload file could not be read completely");
  }

  return materialized;
}
